import {
	createTheme,
	PaletteColorOptions,
	PaletteOptions,
	ThemeOptions
} from '@mui/material';
import { GridDensityTypes } from '@mui/x-data-grid';
import { compact, isNumber } from 'lodash';
import merge from 'lodash/merge';

// needed for MUI data grid types
import type {} from '@mui/x-data-grid-pro/themeAugmentation';
import type {} from '@mui/x-data-grid/themeAugmentation';

const getColor = (color: string, percentage?: number) => {
	if (isNumber(percentage)) {
		return `${color} ${percentage}%`;
	}
	return '';
};

interface IDescopeGradient {
	descopeGradient: (
		pallette: PaletteOptions,
		angle: number,
		firstOffset?: number,
		secondOffset?: number,
		thirdOffset?: number
	) => string;
}

declare module '@mui/material/styles' {
	interface PaletteOptions {
		brand: IDescopeGradient & {
			first: string;
			second: string;
			third: string;
		};
		border: PaletteColorOptions;
		card: PaletteColorOptions;
		wizard: {
			card: PaletteColorOptions & { disabled: any; secondary: any };
		};
		layout: {
			background: any;
		};
		multiline: {
			background: any;
		};
	}
}

export const descopeGradient = (
	palette: PaletteOptions,
	angle: number,
	firstOffset?: number,
	secondOffset?: number,
	thirdOffset?: number
) => {
	const firstColor = getColor(palette.brand.first, firstOffset);
	const secondColor = getColor(palette.brand.second, secondOffset);
	const thirdColor = getColor(palette.brand.third, thirdOffset);
	const colors = compact([firstColor, secondColor, thirdColor]).join(', ');
	return `linear-gradient(${angle}deg, ${colors})`;
};

export const createBaseTheme = (options: ThemeOptions, ...args: object[]) =>
	createTheme(
		merge<ThemeOptions, ThemeOptions>(
			{
				components: {
					MuiDataGrid: {
						defaultProps: {
							disableSelectionOnClick: true,
							density: GridDensityTypes.Comfortable,
							rowHeight: 38
						}
					},
					MuiInputBase: {
						defaultProps: {
							size: 'small'
						}
					},
					MuiButton: {
						styleOverrides: {
							root: {
								borderRadius: '6px'
							}
						},
						defaultProps: {
							size: 'small'
						}
					},
					MuiTooltip: {
						styleOverrides: {
							tooltip: {
								fontSize: '14px',
								borderRadius: '6px'
							}
						},
						defaultProps: {
							enterTouchDelay: 0
						}
					},
					MuiFormLabel: {
						styleOverrides: {
							root: {
								'&.Mui-focused': {
									color: 'currentcolor'
								}
							}
						}
					},
					MuiDialog: {
						defaultProps: {
							fullWidth: true,
							disableRestoreFocus: true
						},
						styleOverrides: {
							scrollPaper: {
								maxHeight: 'calc(100vh - 40px)'
							}
						}
					},
					MuiAutocomplete: {
						defaultProps: {
							fullWidth: true,
							autoHighlight: true,
							disableCloseOnSelect: true,
							openOnFocus: true,
							filterSelectedOptions: false,
							handleHomeEndKeys: true,
							autoSelect: false,
							ChipProps: {
								variant: 'outlined',
								size: 'small'
							}
						}
					},
					MuiChip: {
						defaultProps: {
							size: 'small'
						}
					},
					MuiTextField: {
						defaultProps: {
							autoComplete: 'off',
							size: 'small'
						}
					},
					MuiAlert: {
						styleOverrides: {
							message: {
								whiteSpace: 'pre-line'
							},
							icon: {
								display: 'flex',
								alignItems: 'center'
							}
						}
					},
					MuiLink: {
						styleOverrides: {
							root: {
								rel: 'noopener', //  prevents the new page from being able to access the window.opener property and ensures it runs in a separate process. Without this, the target page can potentially redirect your page to a malicious URL.
								underline: 'always', // Accessibility
								'&.disabled': {
									color: options.palette?.action?.disabled,
									pointerEvents: 'none'
								}
							}
						}
					}
				},
				breakpoints: {
					values: {
						xs: 0,
						sm: 600,
						md: 900,
						lg: 1200,
						xl: 1536
					}
				}
			},
			options
		),
		args
	);

export const TOP_PANE_BAR_HEIGHT = 48;
export const TOP_PANE_BAR_LOW_RES_ALERT_HEIGHT = 56;
export const SIDE_BAR_WIDTH = 256;
